// Domain constants — single source of truth shared by the API and the web client.
// Mirrors the original Delvero design prototype (crm/data.js).

export const LEAD_STATUSES = [
  { id: 'nowy', label: 'Nowy', color: '#8A8D9C' },
  { id: 'mail', label: 'Cold mail', color: '#5E9DFF' },
  { id: 'followup', label: 'Follow-up', color: '#F5A524' },
  { id: 'call', label: 'Call umówiony', color: '#9B8CFF' },
  { id: 'rozmowa', label: 'Po rozmowie', color: '#37C8D8' },
  { id: 'oferta', label: 'Oferta', color: '#FB8C42' },
  { id: 'sub', label: 'Sub aktywny', color: '#2ED393' },
  { id: 'lost', label: 'Odrzucony', color: '#F2555A' },
] as const;

export type LeadStatus = (typeof LEAD_STATUSES)[number]['id'];

export const LEAD_STATUS_IDS = LEAD_STATUSES.map((s) => s.id) as [LeadStatus, ...LeadStatus[]];

/** Statuses whose leads belong in the "Dzwonienie" (calling) queue. Ordered by priority. */
export const CALLABLE_STATUSES = ['nowy', 'mail', 'followup', 'call'] as const satisfies readonly LeadStatus[];

export const BILLING_OPTIONS = [
  { id: 'monthly', label: 'Miesięczny', factor: 1, hint: '' },
  { id: 'quarterly', label: 'Kwartalny', factor: 11 / 12, hint: '1 mc gratis / rok' },
  { id: 'annual', label: 'Roczny', factor: 0.75, hint: '−25%' },
] as const;

export type Billing = (typeof BILLING_OPTIONS)[number]['id'];
export const BILLING_IDS = BILLING_OPTIONS.map((b) => b.id) as [Billing, ...Billing[]];

export const ACTIVITY_TYPES = ['call', 'email', 'note', 'status'] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

/** Default plans for a freshly created application (PPOŻ Manager pricing). */
export const DEFAULT_PLANS = [
  { name: 'Start', price: 149 },
  { name: 'Pro', price: 299 },
  { name: 'Max', price: 599 },
] as const;

/** Call outcomes surfaced in the calling view; each maps to a resulting lead status. */
export const CALL_OUTCOMES = [
  { id: 'no_answer', label: 'Nie odebrał', status: null },
  { id: 'callback', label: 'Oddzwonić później', status: 'followup' },
  { id: 'demo', label: 'Umówione demo', status: 'call' },
  { id: 'interested', label: 'Zainteresowany — oferta', status: 'oferta' },
  { id: 'rejected', label: 'Odrzucił', status: 'lost' },
  { id: 'sub', label: 'Kupił subskrypcję', status: 'sub' },
] as const satisfies readonly { id: string; label: string; status: LeadStatus | null }[];

export const PRE_LEAD_COLOR = '#8A8D9C';

export function billingFactor(billing: Billing): number {
  return BILLING_OPTIONS.find((b) => b.id === billing)?.factor ?? 1;
}

export function statusMeta(status: LeadStatus) {
  return LEAD_STATUSES.find((s) => s.id === status) ?? LEAD_STATUSES[0];
}
